import { useMutation } from '@apollo/client';
import {
  namedOperations,
  UpdateAssetDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateAssetTRPCInput } from './useUpdateAssetTRPC';
import { useUpdateAssetTRPC } from './useUpdateAssetTRPC';

export const useUpdateAsset = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateAssetGraphQL, graphqlState] = useMutation(UpdateAssetDocument, {
    update: (cache) => {
      evictField(cache, 'asset');
      evictField(cache, 'asset_aggregate');
    },
    refetchQueries: [namedOperations.Query.getAssetById],
  });

  const trpcMutation = useUpdateAssetTRPC();

  const updateAsset = async (
    variables: UpdateAssetTRPCInput,
    _options?: {
      confirmChangeRequest?: boolean;
      hasFileChanges?: boolean;
    }
  ) => {
    if (trpcEnabled) {
      return trpcMutation.updateAsset(variables);
    }

    const result = await updateAssetGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update asset');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateAsset,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateAsset,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
